import { useCallback, useEffect, useMemo, useState } from "react";
import {
  getDate,
  getDay,
  getMonth,
  getYear,
  setDate,
  setMonth,
} from "../../../shared/utils/date";

interface UseCalendarLogicProps {
  value: Date | null;
  noDefaultValue?: boolean;
  onTempChange?: (date: Date | null) => void;
}

export type UseCalendarLogicResult = {
  visibleMonth: Date;
  temp: Date | null;
  setTempValue: (date: Date | null) => void;
  gridDays: { date: Date; inMonth: boolean }[];
  handlePrevMonth: () => void;
  handleNextMonth: () => void;
  isSameDay: (a?: Date | null, b?: Date | null) => boolean;
};

export const useCalendarLogic = ({
  value,
  noDefaultValue,
  onTempChange,
}: UseCalendarLogicProps): UseCalendarLogicResult => {
  const [visibleMonth, setVisibleMonth] = useState<Date>(value ?? new Date());
  const [temp, setTemp] = useState<Date | null>(
    noDefaultValue ? null : (value ?? new Date()),
  );

  const setTempValue = useCallback(
    (date: Date | null) => {
      setTemp(date);
      onTempChange?.(date);
    },
    [onTempChange],
  );

  useEffect(() => {
    if (value) {
      setVisibleMonth(value);
      setTempValue(value);
    }
  }, [setTempValue, value]);

  const handlePrevMonth = useCallback(() => {
    const d = new Date(visibleMonth);
    setMonth(d, getMonth(d) - 1);
    setVisibleMonth(d);
  }, [visibleMonth]);

  const handleNextMonth = useCallback(() => {
    const d = new Date(visibleMonth);
    setMonth(d, getMonth(d) + 1);
    setVisibleMonth(d);
  }, [visibleMonth]);

  const startOfMonth = useMemo(() => {
    const d = new Date(visibleMonth);
    setDate(d, 1);

    return d;
  }, [visibleMonth]);

  const firstWeekday = useMemo(() => {
    const jsDay = getDay(startOfMonth);

    return (jsDay + 6) % 7;
  }, [startOfMonth]);

  const daysInMonth = useMemo(() => {
    const d = new Date(visibleMonth);
    setMonth(d, getMonth(d) + 1);
    setDate(d, 0);

    return getDate(d);
  }, [visibleMonth]);

  const gridDays = useMemo(() => {
    const result: { date: Date; inMonth: boolean }[] = [];
    // Previous month padding
    for (let i = 0; i < firstWeekday; i++) {
      const d = new Date(startOfMonth);
      setDate(d, getDate(d) - (firstWeekday - i));
      result.push({ date: d, inMonth: false });
    }
    // Current month days
    for (let i = 1; i <= daysInMonth; i++) {
      const d = new Date(startOfMonth);
      setDate(d, i);
      result.push({ date: d, inMonth: true });
    }
    // Next month padding
    const getLastDate = (): Date =>
      result.length > 0
        ? result[result.length - 1]!.date
        : new Date(startOfMonth);

    while (result.length % 7 !== 0) {
      const d = new Date(getLastDate());
      setDate(d, getDate(d) + 1);
      result.push({ date: d, inMonth: false });
    }
    while (result.length < 42) {
      const d = new Date(getLastDate());
      setDate(d, getDate(d) + 1);
      result.push({ date: d, inMonth: false });
    }

    return result;
  }, [firstWeekday, daysInMonth, startOfMonth]);

  const isSameDay = (a?: Date | null, b?: Date | null) =>
    !!a &&
    !!b &&
    getYear(a) === getYear(b) &&
    getMonth(a) === getMonth(b) &&
    getDate(a) === getDate(b);

  return {
    visibleMonth,
    temp,
    setTempValue,
    gridDays,
    handlePrevMonth,
    handleNextMonth,
    isSameDay,
  };
};
